import React, { useState } from 'react';
import { customOrderService } from '../services/firestore';
import { useNavigate } from 'react-router-dom';
import './CustomOrderPage.css';

const CustomOrderPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    customerName: '',
    customerPhone: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showNotification, setShowNotification] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.customerName.trim()) {
      newErrors.customerName = 'Name is required';
    }

    if (!formData.customerPhone.trim()) {
      newErrors.customerPhone = 'Phone number is required';
    } else if (!/^[0-9]{10}$/.test(formData.customerPhone.replace(/\s/g, ''))) {
      newErrors.customerPhone = 'Please enter a valid 10-digit phone number';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await customOrderService.create({
        customerName: formData.customerName.trim(),
        customerPhone: formData.customerPhone.trim(),
        description: formData.description.trim(),
        status: 'pending'
      });

      setShowNotification(true);
      setTimeout(() => {
        setShowNotification(false);
        navigate('/');
      }, 3000);

      // Reset form
      setFormData({
        customerName: '',
        customerPhone: '',
        description: ''
      });

    } catch (error) {
      console.error('Error submitting custom order:', error);
      setErrors({
        submit: 'Failed to submit your request. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="custom-order-page">
      <div className="container">
        <div className="custom-order-header">
          <h1>Custom Order</h1>
          <p>
            Have a specific design or requirement? Let us know what you need, 
            and our team will get back to you with a custom quote.
          </p>
        </div>

        <div className="custom-order-content">
          <div className="form-section">
            <form onSubmit={handleSubmit} className="custom-order-form">
              <div className="form-group">
                <label htmlFor="customerName" className="form-label">
                  Customer Name *
                </label>
                <input
                  type="text"
                  id="customerName"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleInputChange}
                  className={`form-input ${errors.customerName ? 'error' : ''}`}
                  placeholder="Enter your full name"
                  disabled={loading}
                />
                {errors.customerName && (
                  <span className="error-message">{errors.customerName}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="customerPhone" className="form-label">
                  Mobile Number *
                </label>
                <input
                  type="tel"
                  id="customerPhone"
                  name="customerPhone"
                  value={formData.customerPhone}
                  onChange={handleInputChange}
                  className={`form-input ${errors.customerPhone ? 'error' : ''}`}
                  placeholder="Enter your 10-digit mobile number"
                  disabled={loading}
                />
                {errors.customerPhone && (
                  <span className="error-message">{errors.customerPhone}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="description" className="form-label">
                  Description *
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className={`form-input form-textarea ${errors.description ? 'error' : ''}`}
                  placeholder="Describe your custom printing requirements in detail (e.g., product type, quantity, design specifications, timeline, etc.)"
                  rows={6}
                  disabled={loading}
                />
                {errors.description && (
                  <span className="error-message">{errors.description}</span>
                )}
              </div>

              {errors.submit && (
                <div className="error-message submit-error">{errors.submit}</div>
              )}

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary btn-large"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="loading"></div>
                      Submitting...
                    </>
                  ) : (
                    'Submit Request'
                  )}
                </button>
                
                <button
                  type="button"
                  className="btn btn-secondary btn-large"
                  onClick={() => navigate('/')}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>

          <div className="info-section">
            <div className="info-card">
              <h3>What happens next?</h3>
              <ol>
                <li>
                  <strong>Review:</strong> Our team will review your custom order request
                </li>
                <li>
                  <strong>Contact:</strong> We'll call you within 24 hours to discuss details
                </li>
                <li>
                  <strong>Quote:</strong> You'll receive a detailed quote based on your requirements
                </li>
                <li>
                  <strong>Production:</strong> Once approved, we'll start production
                </li>
                <li>
                  <strong>Delivery:</strong> Your custom order will be delivered as promised
                </li>
              </ol>
            </div>

            <div className="info-card">
              <h3><span className="icon-custom"></span> Popular Custom Orders</h3>
              <ul>
                <li><span className="icon-custom"></span> Company logos on merchandise</li>
                <li><span className="icon-custom"></span> Event-specific designs</li>
                <li><span className="icon-bulk"></span> Bulk corporate orders</li>
                <li><span className="icon-custom"></span> Personalized gifts</li>
                <li><span className="icon-bulk"></span> Custom packaging</li>
                <li><span className="icon-custom"></span> Promotional materials</li>
              </ul>
            </div>

            <div className="contact-info">
              <h4>Need help?</h4>
              <p>Call us directly at: <strong>+91 98765 43210</strong></p>
              <p>Email: <strong>info@jkprints.com</strong></p>
            </div>
          </div>
        </div>
      </div>

      {/* Success Notification */}
      {showNotification && (
        <div className="notification-toast success">
          <div className="notification-content">
            <span className="notification-icon">check</span>
            <div>
              <strong>Request Submitted!</strong>
              <p>We'll contact you within 24 hours.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomOrderPage;
